import {
  Bot,
  Copy,
  FileText,
  Menu,
  Paperclip,
  Plus,
  RefreshCw,
  Search,
  Send,
  Settings,
  Share2,
  Sparkles,
  UserRound
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChainOfThought,
  ChainOfThoughtItem,
  ChatContainerContent,
  ChatContainerRoot,
  FeedbackBar,
  FileUpload,
  Markdown,
  Message,
  MessageAction,
  MessageActions,
  MessageAvatar,
  MessageContent,
  PromptInput,
  PromptInputAction,
  PromptInputActions,
  PromptInputTextarea,
  Steps,
  ThinkingBar
} from "./components/prompt-kit";
import {
  QUESTIONS,
  answeredQuestions,
  calculate,
  chooseAnswer,
  compactUsd,
  currentQuestion,
  number,
  optionFor,
  optionFromText,
  usd,
  type CalculatorInputs,
  type CalculatorQuestion,
  type Estimate,
  type OptionId
} from "./lib/calculator";
import { fallbackOrchestration, requestOrchestration, type OrchestrationState } from "./lib/orchestration";

type ChatMessage = {
  id: string;
  role: "assistant" | "user";
  text: string;
  helper?: string;
};

type RecentCalculation = {
  id: string;
  title: string;
  meta: string;
  inputs: CalculatorInputs;
};

const RECENT: RecentCalculation[] = [
  {
    id: "support",
    title: "Customer Support Ops",
    meta: "May 12, 2026 - 2:34 PM",
    inputs: {
      workType: "customer-support",
      humanMembers: "humans-26-50",
      agentMembers: "agents-16-40",
      tasks: "tasks-10000",
      persistence: "organization-memory",
      runtime: "standard-runtime",
      modelMix: "balanced-mix",
      autonomy: "approval-execute",
      risk: "customer-risk"
    }
  },
  {
    id: "back-office",
    title: "Back Office Automation",
    meta: "May 11, 2026 - 11:08 AM",
    inputs: {
      workType: "back-office",
      humanMembers: "humans-51-100",
      agentMembers: "agents-41-100",
      tasks: "tasks-50000",
      persistence: "organization-memory",
      runtime: "dedicated-runtime",
      modelMix: "advanced-mix",
      autonomy: "approval-execute",
      risk: "financial-risk"
    }
  },
  {
    id: "IT",
    title: "IT Helpdesk",
    meta: "May 9, 2026 - 10:15 AM",
    inputs: {
      workType: "it-operations",
      humanMembers: "humans-101-250",
      agentMembers: "agents-41-100",
      tasks: "tasks-50000",
      persistence: "regulated-memory",
      runtime: "regulated-runtime",
      modelMix: "frontier-mix",
      autonomy: "guarded-execute",
      risk: "regulated-risk"
    }
  }
];

export default function App() {
  const [inputs, setInputs] = useState<CalculatorInputs>({});
  const [prompt, setPrompt] = useState("");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<"helpful" | "not-helpful" | null>(null);
  const [feedbackClosed, setFeedbackClosed] = useState(false);
  const [orchestration, setOrchestration] = useState<OrchestrationState>(() =>
    fallbackOrchestration({}, calculate({}), currentQuestion({}))
  );
  const [orchestrationNonce, setOrchestrationNonce] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const chatRootRef = useRef<HTMLDivElement | null>(null);

  const estimate = useMemo(() => calculate(inputs), [inputs]);
  const activeQuestion = useMemo(() => currentQuestion(inputs), [inputs]);
  const messages = useMemo(() => buildMessages(inputs, estimate, orchestration), [inputs, estimate, orchestration]);

  useEffect(() => {
    const controller = new AbortController();
    const fallback = fallbackOrchestration(inputs, estimate, activeQuestion);

    setOrchestration({ ...fallback, modelStatus: "loading" });
    requestOrchestration(inputs, estimate, activeQuestion, controller.signal).then((next) => {
      if (!controller.signal.aborted) setOrchestration(next);
    });

    return () => controller.abort();
  }, [inputs, estimate, activeQuestion, orchestrationNonce]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const root = chatRootRef.current;
      const finalResult = root?.querySelector<HTMLElement>('[data-testid="final-result"]');
      if (root && estimate.complete && finalResult) {
        const rootTop = root.getBoundingClientRect().top;
        const finalTop = finalResult.getBoundingClientRect().top;
        root.scrollTo({ top: Math.max(0, root.scrollTop + finalTop - rootTop - 24), behavior: "auto" });
      } else if (root) {
        root.scrollTo({ top: root.scrollHeight, behavior: "smooth" });
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [messages.length, activeQuestion?.id, estimate.complete, orchestration.summary]);

  const reset = () => {
    setInputs({});
    setPrompt("");
    setAttachments([]);
    setFeedback(null);
    setFeedbackClosed(false);
  };

  const choose = (question: CalculatorQuestion, optionId: OptionId) => {
    setInputs((current) => chooseAnswer(current, question.id, optionId));
    setFeedback(null);
    setFeedbackClosed(false);
  };

  const submitPrompt = () => {
    const text = prompt.trim();
    if (!text) return;

    if (!activeQuestion) {
      if (/new|reset|start over/i.test(text)) reset();
      setPrompt("");
      return;
    }

    const optionId = optionFromText(activeQuestion, text);
    if (optionId) choose(activeQuestion, optionId);
    setPrompt("");
  };

  return (
    <div className="shift-app" data-testid="shift-app">
      <aside className="shift-sidebar" aria-label="Calculation history">
        <div className="sidebar-top">
          <div className="brand-lockup">
            <ShiftMark />
            <span>THE SHIFT</span>
          </div>
          <button className="icon-button" aria-label="Collapse history">
            <Menu size={16} />
          </button>
        </div>

        <button className="new-calculation" onClick={reset}>
          <Plus size={18} />
          <span>New Calculation</span>
        </button>

        <div className="history-list">
          <p className="sidebar-label">Recent calculations</p>
          {RECENT.map((item) => (
            <button key={item.id} className="history-item" onClick={() => setInputs(item.inputs)}>
              <span className="history-icon" aria-hidden>
                <Bot size={18} />
              </span>
              <span>
                <strong>{item.title}</strong>
                <small>{item.meta}</small>
              </span>
            </button>
          ))}
        </div>

        <div className="sidebar-footer">
          <button>
            <Settings size={16} />
            <span>Settings</span>
          </button>
          <button className="icon-button" aria-label="Display mode">
            <Sparkles size={16} />
          </button>
        </div>
      </aside>

      <main className="shift-main">
        <header className="shift-header">
          <div>
            <p>calculator.l1fe.ai</p>
            <h1 data-testid="shift-title">THE SHIFT</h1>
          </div>
          <div className="header-actions">
            <span>{estimate.progress}% scoped</span>
            <button type="button">
              <Share2 size={16} />
              Share
            </button>
          </div>
        </header>

        <ChatContainerRoot className="conversation" ref={chatRootRef}>
          <ChatContainerContent>
            {messages.map((message, index) => (
              <ConversationMessage
                key={message.id}
                message={message}
                estimate={estimate}
                showTrace={index === messages.length - 1 && estimate.answeredCount >= 4}
              />
            ))}

            {activeQuestion ? (
              <ChoiceGroup question={activeQuestion} selected={inputs[activeQuestion.id]} onChoose={choose} />
            ) : (
              <FinalResult
                estimate={estimate}
                orchestrationSummary={orchestration.summary}
                feedback={feedback}
                setFeedback={setFeedback}
                closed={feedbackClosed}
                setClosed={setFeedbackClosed}
              />
            )}
          </ChatContainerContent>
        </ChatContainerRoot>

        <FileUpload inputRef={fileInputRef} onFiles={setAttachments}>
          <PromptInput>
            {attachments.length > 0 && (
              <div className="attachment-row" aria-label="Attached files">
                {attachments.map((file) => (
                  <span key={file}>
                    <FileText size={14} />
                    {file}
                  </span>
                ))}
              </div>
            )}
            <PromptInputTextarea
              id="shift-prompt"
              value={prompt}
              onChange={setPrompt}
              onSubmit={submitPrompt}
              label={activeQuestion ? activeQuestion.prompt : "Ask a question or update your inputs"}
              placeholder={activeQuestion ? "Type an answer or choose an option..." : "Ask a question or update your inputs..."}
            />
            <PromptInputActions>
              <PromptInputAction label="Attach file" onClick={() => fileInputRef.current?.click()}>
                <Paperclip size={17} />
              </PromptInputAction>
              <PromptInputAction label="Search context">
                <Search size={17} />
              </PromptInputAction>
              <span className="prompt-status">Pricing tools ready</span>
              <button className="send-button" type="button" aria-label="Send answer" disabled={!prompt.trim()} onClick={submitPrompt}>
                <Send size={17} />
              </button>
            </PromptInputActions>
          </PromptInput>
        </FileUpload>
      </main>

      <aside className="estimate-rail" aria-label="Live estimate">
        <RailCards estimate={estimate} orchestration={orchestration} onRetry={() => setOrchestrationNonce((current) => current + 1)} />
      </aside>
    </div>
  );
}

function ConversationMessage({ message, estimate, showTrace }: { message: ChatMessage; estimate: Estimate; showTrace: boolean }) {
  const isAssistant = message.role === "assistant";

  return (
    <Message role={message.role}>
      {isAssistant && (
        <MessageAvatar>
          <Bot size={17} />
        </MessageAvatar>
      )}
      <MessageContent>
        <Markdown text={message.text} />
        {message.helper && <p className="message-helper">{message.helper}</p>}
        {showTrace && <VisibleTrace estimate={estimate} />}
      </MessageContent>
      {!isAssistant && (
        <MessageAvatar>
          <UserRound size={17} />
        </MessageAvatar>
      )}
      <MessageActions>
        <MessageAction label="Copy">
          <Copy size={14} />
        </MessageAction>
      </MessageActions>
    </Message>
  );
}

function ChoiceGroup({
  question,
  selected,
  onChoose
}: {
  question: CalculatorQuestion;
  selected?: OptionId;
  onChoose: (question: CalculatorQuestion, optionId: OptionId) => void;
}) {
  return (
    <section className="choice-group" data-testid={`question-${question.id}`}>
      <div className="choice-helper">{question.helper}</div>
      <div className="choice-grid">
        {question.options.map((option) => (
          <button
            key={option.id}
            className={selected === option.id ? "choice selected" : "choice"}
            onClick={() => onChoose(question, option.id)}
          >
            <span>{option.label}</span>
            <small>{option.description}</small>
          </button>
        ))}
      </div>
    </section>
  );
}

function RailCards({
  estimate,
  orchestration,
  onRetry
}: {
  estimate: Estimate;
  orchestration: OrchestrationState;
  onRetry: () => void;
}) {
  const taskDelta = estimate.humanAverageCostPerTask > 0 ? 1 - estimate.costPerTask / estimate.humanAverageCostPerTask : 0;
  const orchestrationText = orchestrationStatusText(orchestration.modelStatus);

  return (
    <>
      <section className="rail-card">
        <ThinkingBar text={estimate.complete ? "Calculation ready" : "Analyzing workforce mix"} complete={estimate.complete} />
        <p className="rail-copy">
          {estimate.complete
            ? "All required answers have visible pricing outputs."
            : "Evaluating tasks, automation potential, and agent configuration."}
        </p>
        <div className="progress-track" aria-label={`Progress ${estimate.progress}%`}>
          <span style={{ width: `${estimate.progress}%` }} />
        </div>
        <div className="progress-meta">
          <span>{estimate.answeredCount} / {QUESTIONS.length} required answers</span>
          <strong>{estimate.progress}%</strong>
        </div>
        <div className={`orchestration-status ${orchestration.modelStatus}`}>
          <span>{orchestrationText}</span>
          {orchestration.modelStatus === "rate_limited" && (
            <button type="button" onClick={onRetry}>
              <RefreshCw size={13} />
              Retry
            </button>
          )}
        </div>
      </section>

      <section className="rail-card plan-card">
        <div className="rail-title-row">
          <h2>Recommended plan</h2>
          <span data-testid="recommended-plan">{estimate.recommendedPlan}</span>
        </div>
        <div className="metric-grid">
          <Metric label="Cost per task" value={usd(estimate.costPerTask, 2)} detail={`${Math.max(0, Math.round(taskDelta * 100))}% below human average`} testId="cost-per-task" />
          <Metric label="Human-equivalent comparison" value={`${number(estimate.humanEquivalentFte, 1)} FTE`} detail="at fully loaded $72K / FTE" testId="human-equivalent" />
          <Metric label="Est. monthly cost" value={usd(estimate.monthlyEstimate)} detail="Includes platform and usage" testId="monthly-estimate" />
          <Metric label="Est. tasks per month" value={number(estimate.tasksPerMonth)} detail="Projected volume" />
        </div>
      </section>

      <section className="rail-card">
        <h2>Steps</h2>
        <Steps steps={estimate.steps} />
      </section>

      <section className="rail-card" data-testid="pricing-tools">
        <h2>Pricing tools</h2>
        <div className="tool-list">
          {estimate.tools.map((tool) => (
            <div key={tool.id} className={`tool-row ${tool.status}`}>
              <span aria-hidden />
              <div>
                <strong>{tool.label}</strong>
                <small>{tool.output}</small>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rail-card">
        <h2>Result summary</h2>
        <div className="summary-grid">
          <Metric label="Total agents" value={String(estimate.agentMembers)} />
          <Metric label="Digital labor" value={`${number(estimate.humanEquivalentFte, 1)} FTE`} />
          <Metric label="Automation potential" value={`${Math.round(estimate.automationPotential * 100)}%`} />
          <Metric label="Annual savings" value={estimate.annualSavings > 0 ? compactUsd(estimate.annualSavings) : "TBD"} />
        </div>
      </section>
    </>
  );
}

function Metric({ label, value, detail, testId }: { label: string; value: string; detail?: string; testId?: string }) {
  return (
    <div className="metric" data-testid={testId}>
      <p>{label}</p>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}

function VisibleTrace({ estimate }: { estimate: Estimate }) {
  return (
    <ChainOfThought title="Open calculation trace">
      {estimate.tools.map((tool) => (
        <ChainOfThoughtItem key={tool.id}>
          <span>{tool.status}</span>
          <strong>{tool.label}</strong>
          <p>{tool.output}</p>
        </ChainOfThoughtItem>
      ))}
    </ChainOfThought>
  );
}

function FinalResult({
  estimate,
  orchestrationSummary,
  feedback,
  setFeedback,
  closed,
  setClosed
}: {
  estimate: Estimate;
  orchestrationSummary: string;
  feedback: "helpful" | "not-helpful" | null;
  setFeedback: (value: "helpful" | "not-helpful") => void;
  closed: boolean;
  setClosed: (value: boolean) => void;
}) {
  return (
    <section className="final-result" data-testid="final-result">
      <div className="final-header">
        <div>
          <p>Recommendation</p>
          <h2>{estimate.recommendedPlan}</h2>
        </div>
        <strong>{usd(estimate.monthlyEstimate)} / mo</strong>
      </div>
      <p className="final-summary">{orchestrationSummary}</p>
      <div className="final-metrics">
        <Metric label="Agent members" value={String(estimate.agentMembers)} />
        <Metric label="Cost per task" value={usd(estimate.costPerTask, 2)} />
        <Metric label="Human-equivalent" value={`${number(estimate.humanEquivalentFte, 1)} FTE`} />
      </div>
      <div className="line-items">
        {estimate.lineItems.map((item) => (
          <div key={item.label}>
            <span>
              <strong>{item.label}</strong>
              <small>{item.detail}</small>
            </span>
            <b>{item.value}</b>
          </div>
        ))}
      </div>
      {!closed && (
        <FeedbackBar
          state={feedback}
          onHelpful={() => setFeedback("helpful")}
          onNotHelpful={() => setFeedback("not-helpful")}
          onClose={() => setClosed(true)}
        />
      )}
    </section>
  );
}

function buildMessages(inputs: CalculatorInputs, estimate: Estimate, orchestration: OrchestrationState): ChatMessage[] {
  const answered = answeredQuestions(inputs);
  const messages: ChatMessage[] = [
    {
      id: "intro",
      role: "assistant",
      text: "Hi. I will estimate the right agent workforce for this shift.",
      helper: QUESTIONS[0]?.prompt
    }
  ];

  answered.forEach((question, index) => {
    const option = optionFor(question.id, inputs[question.id]);
    if (!option) return;

    messages.push({ id: `${question.id}-answer`, role: "user", text: option.label });

    const nextQuestion = QUESTIONS[index + 1];
    if (nextQuestion) {
      messages.push({
        id: `${nextQuestion.id}-prompt`,
        role: "assistant",
        text: index === 0 ? "Good. I will use that operating pattern." : "Updated. The estimate has been recalculated.",
        helper: nextQuestion.prompt
      });
    }
  });

  if (estimate.complete) {
    messages.push({
      id: "complete",
      role: "assistant",
      text: orchestration.summary,
      helper: "The trace shows visible pricing-tool outputs only."
    });
  }

  return messages;
}

function orchestrationStatusText(status: OrchestrationState["modelStatus"]): string {
  switch (status) {
    case "loading":
      return "Orchestration syncing.";
    case "model_orchestrated":
      return "Orchestration ready.";
    case "rate_limited":
      return "Orchestration delayed. Deterministic tools remain active.";
    case "idle":
    case "deterministic_fallback":
    default:
      return "Deterministic tools active.";
  }
}

function ShiftMark() {
  return (
    <svg className="shift-mark" viewBox="0 0 48 48" aria-hidden>
      <rect x="5" y="5" width="38" height="38" rx="11" />
      <path d="M15 31L31 15M22 15h9v9M17 35h14" />
    </svg>
  );
}
